import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Search, Trash2, Download, Eye, ArrowRight, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Users, Send, FileSpreadsheet, ImageUp } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 20;

const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-500",
  pending: "bg-slate-100 text-slate-600",
  printing: "bg-blue-100 text-blue-700",
  framing: "bg-amber-100 text-amber-700",
  dispatched: "bg-emerald-100 text-emerald-700",
};

function StatusBadge({ status, label }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.pending}`}>
      {label}
    </span>
  );
}

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [flow, setFlow] = useState(["pending", "printing", "framing", "dispatched"]);
  const [labels, setLabels] = useState({});
  const [mrId, setMrId] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [summary, setSummary] = useState([]);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const processedInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);

  const pickProcessedPhoto = (r) => {
    setUploadTarget(r);
    processedInputRef.current?.click();
  };

  const onProcessedFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const r = uploadTarget;
    setUploadTarget(null);
    if (!file || !r) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) {
      toast.error("Please pick a PNG, JPG or WEBP image");
      return;
    }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image too large (max 20MB)"); return; }
    setBusyId(r.id);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const { data } = await api.post(`/admin/requests/${r.id}/processed-photo`, fd);
      setRequests((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...data, created_by_region: x.created_by_region } : x)));
      toast.success("Certificate regenerated with the background-removed photo");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusyId(null);
    }
  };

  useEffect(() => {
    api.get("/admin/employees?page_size=500").then(({ data }) => setEmployees(data.data)).catch(() => {});
    api.get("/requests-meta").then(({ data }) => { setFlow(data.flow); setLabels(data.labels); }).catch(() => {});
    loadSummary();
  }, []);

  const loadSummary = () => {
    api.get("/admin/requests/summary").then(({ data }) => setSummary(data)).catch(() => {});
  };

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ mine: "false", page: String(page), page_size: String(PAGE_SIZE) });
    if (mrId !== "all") params.set("mr_id", mrId);
    params.set("status", statusFilter === "all" ? "draft,pending" : statusFilter);
    if (q.trim()) params.set("q", q.trim());
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/requests?${params}`)
      .then(({ data }) => { setRequests(data.data); setTotal(data.total); })
      .catch(() => toast.error("Failed to load requests"))
      .finally(() => setLoading(false));
  };

  // Reset to first page whenever a filter changes (no-op re-render if already 1)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [mrId, statusFilter, dateFrom, dateTo, q]);

  // Debounced load (covers typing in the search box)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [page, mrId, statusFilter, dateFrom, dateTo, q]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const nextStatus = (status) => {
    if (status === "draft") return null;
    const i = flow.indexOf(status);
    return i >= 0 && i < flow.length - 1 ? flow[i + 1] : null;
  };

  const setStatus = async (r, status) => {
    if (!status || status === r.status) return;
    if (status === "printing" && !r.has_processed_photo &&
        !window.confirm("No background-removed photo uploaded — send for printing anyway?")) return;
    setBusyId(r.id);
    try {
      const { data } = await api.patch(`/admin/requests/${r.id}/status`, { status });
      setRequests((prev) => prev.map((x) => x.id === r.id ? { ...x, ...data, created_by_region: x.created_by_region } : x));
      toast.success(`${data.display_id || r.display_id || "Request"} → ${data.status_label}`);
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setBusyId(null);
    }
  };

  const submitDraft = (r) => {
    if (!window.confirm(`Generate the official ID for this draft and submit it? This uses one of ${r.created_by_name}'s 6 request slots.`)) return;
    setStatus(r, "pending");
  };

  // kind: "card" (full JPG) | "pdf" | "photo" (original doctor photo)
  const downloadFile = async (r, kind) => {
    setBusyId(r.id);
    try {
      let url, filename;
      if (kind === "card") {
        url = `${API}/requests/${r.id}/download`;
        filename = `${r.display_id}_${r.doctor_name.replace(/ /g, "_")}.jpg`;
      } else if (kind === "pdf") {
        url = `${API}/requests/${r.id}/download?fmt=pdf`;
        filename = `${r.display_id}_${r.doctor_name.replace(/ /g, "_")}.pdf`;
      } else if (kind === "photo-processed") {
        url = `${API}/requests/${r.id}/photo?kind=processed`;
        filename = `${r.display_id}_photo_processed.png`;
      } else {
        url = `${API}/requests/${r.id}/photo`;
        filename = `${r.display_id}_photo.jpg`;
      }
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Download failed");
        return;
      }
      const dispo = res.headers.get("content-disposition") || "";
      const m = dispo.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success("Download started");
    } catch {
      toast.error("Download failed");
    } finally {
      setBusyId(null);
    }
  };

  const exportRequests = async () => {
    const params = new URLSearchParams({ fmt: "xlsx" });
    if (mrId !== "all") params.set("mr_id", mrId);
    params.set("status", statusFilter === "all" ? "draft,pending" : statusFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    try {
      const res = await fetch(`${API}/requests/export?${params}`, { credentials: "include" });
      if (!res.ok) { toast.error("Export failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const who = mrId !== "all" ? (mrOptions.find((e) => e.id === mrId)?.emp_code || "mr") : "all_mrs";
      a.download = `certificate_requests_${who}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export started");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this request permanently? This removes the certificate and photo.")) return;
    try {
      await api.delete(`/requests/${id}`);
      setRequests((prev) => prev.filter((r) => r.id !== id));
      setTotal((t) => Math.max(0, t - 1));
      loadSummary();
      toast.success("Request deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const drillToMr = (id) => {
    setMrId(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const mrOptions = employees.filter((e) => e.role === "mr");

  return (
    <Shell role="admin">
      <input ref={processedInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={onProcessedFile} />
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Certificate Requests</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Incoming Requests</h1>
          <p className="text-slate-500 mt-1 text-sm">{total} submission{total !== 1 ? "s" : ""}{mrId !== "all" && " · filtered to 1 MR"}</p>
          <p className="text-slate-400 mt-0.5 text-xs">Requests move to Certificates Processed once sent for printing.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-1.5" onClick={() => setSummaryOpen((v) => !v)}>
            <Users className="w-4 h-4" /> MR-wise <ChevronDown className={`w-3.5 h-3.5 transition ${summaryOpen ? "rotate-180" : ""}`} />
          </Button>
          <Button variant="outline" className="gap-1.5" onClick={exportRequests}>
            <FileSpreadsheet className="w-4 h-4" /> Export Excel
          </Button>
        </div>
      </div>

      {/* MR-wise summary */}
      {summaryOpen && (
        <Card className="p-0 mb-4 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
            MR-wise submissions — click a row to view that MR's certificates
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-white sticky top-0 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5">MR</th>
                  <th className="px-4 py-2.5 text-right">Total</th>
                  <th className="px-4 py-2.5 text-right">Pending</th>
                  <th className="px-4 py-2.5 text-right">In&nbsp;Progress</th>
                  <th className="px-4 py-2.5 text-right">Dispatched</th>
                  <th className="px-4 py-2.5">Last</th>
                </tr>
              </thead>
              <tbody>
                {summary.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-6 text-slate-400">No submissions yet</td></tr>
                )}
                {summary.map((s) => (
                  <tr
                    key={s.mr_id}
                    onClick={() => drillToMr(s.mr_id)}
                    className={`border-b border-slate-100 cursor-pointer hover:bg-blue-50 ${mrId === s.mr_id ? "bg-blue-50" : ""}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900">{s.name}</div>
                      <div className="text-xs text-slate-400">{s.emp_code}</div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{s.total}</td>
                    <td className="px-4 py-2.5 text-right text-slate-600">{s.pending}</td>
                    <td className="px-4 py-2.5 text-right text-blue-700">{s.in_progress}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-700">{s.dispatched}</td>
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap text-xs">{s.last ? new Date(s.last).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <Select value={mrId} onValueChange={setMrId}>
            <SelectTrigger className="h-11 md:w-52"><SelectValue placeholder="All MRs" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All MRs</SelectItem>
              {mrOptions.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name} ({e.emp_code})</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11 md:w-48"><SelectValue placeholder="All incoming" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All incoming</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by request ID, doctor or MR…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-11"
            />
          </div>

          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-11 w-36" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-11 w-36" />
            </div>
            {(dateFrom || dateTo) && (
              <Button variant="ghost" className="h-11 text-slate-500" onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear</Button>
            )}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Request ID</th>
                <th className="px-4 py-3">Doctor</th>
                <th className="px-4 py-3">Speciality</th>
                <th className="px-4 py-3">MR</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">Loading...</td></tr>
              )}
              {!loading && requests.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-slate-400">No requests found.</td></tr>
              )}
              {!loading && requests.map((r) => {
                const nxt = nextStatus(r.status);
                const isBusy = busyId === r.id;
                const isDraft = r.status === "draft";
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 align-middle">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-900">{r.display_id || <span className="text-slate-300 italic font-sans font-normal">draft</span>}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {r.doctor_name}
                      {r.has_processed_photo && (
                        <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 align-middle">BG ✓</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{r.speciality || "-"}</div>
                      <div className="text-xs text-slate-400">{r.mobile_number || ""}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/mr/${r.created_by_id}`} className="text-slate-900 text-sm font-medium hover:text-blue-600 hover:underline">{r.created_by_name}</Link>
                      <div className="text-xs text-slate-400">{r.created_by_emp_code}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.created_by_region || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={r.status} label={r.status_label} />
                        {!isDraft && (
                          <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                            <SelectTrigger className="h-7 w-7 p-0 border-none shadow-none [&>svg]:opacity-50" aria-label="Change status" />
                            <SelectContent>
                              {flow.map((s) => (
                                <SelectItem key={s} value={s}>{labels[s] || s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString()}<br/>
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {isDraft ? (
                          <Button
                            size="sm" className="h-8 gap-1 text-xs bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => submitDraft(r)} disabled={isBusy}
                            title="Generate ID & submit on the MR's behalf"
                          >
                            <Send className="w-3.5 h-3.5" /> Submit
                          </Button>
                        ) : nxt ? (
                          <Button
                            size="sm" className="h-8 gap-1 text-xs bg-brand-800 hover:bg-brand-700"
                            onClick={() => setStatus(r, nxt)} disabled={isBusy}
                            title={`Advance to ${labels[nxt] || nxt}`}
                          >
                            <ArrowRight className="w-3.5 h-3.5" /> {labels[nxt] || nxt}
                          </Button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 px-2">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Done
                          </span>
                        )}
                        <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setPreviewId(r.id)} title="Preview">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm" variant="outline" className="h-8 w-8 p-0"
                          onClick={() => pickProcessedPhoto(r)} disabled={isBusy || isDraft}
                          title={isDraft ? "Submit first" : r.has_processed_photo ? "Replace background-removed photo" : "Upload background-removed photo"}
                        >
                          <ImageUp className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8 px-2 gap-0.5" disabled={isBusy || isDraft} title={isDraft ? "Submit first to download" : "Download"}>
                              <Download className="w-4 h-4" />
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuLabel className="text-xs">{r.display_id}</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => downloadFile(r, "pdf")}>Certificate (PDF)</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadFile(r, "card")}>Certificate (JPG)</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => downloadFile(r, "photo")}>Original Photo</DropdownMenuItem>
                            {r.has_processed_photo && (
                              <DropdownMenuItem onClick={() => downloadFile(r, "photo-processed")}>Processed Photo</DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button
                          size="sm" variant="ghost"
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(r.id)} title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50/50">
          <div className="text-xs text-slate-500">
            {total} {total === 1 ? "request" : "requests"}
            {totalPages > 1 && <> · page {page} of {totalPages}</>}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Preview dialog */}
      <Dialog open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Certificate Preview</DialogTitle>
          </DialogHeader>
          {previewId && (
            <img
              src={`${API}/requests/${previewId}/preview?t=${Date.now()}`}
              alt="Certificate preview"
              className="w-full rounded-md border border-slate-200"
            />
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
